import { css } from "@emotion/css";
import { Text } from "components";
import { CalendarBlankIcon, CheckIcon, CaretRightIcon } from "components/icons";
import { UserContext } from "context";
import { differenceInDays, format } from "date-fns";
import { useContext, useEffect, useState } from "react";
import { recordActivity } from "services";
import { EActivityEvent, T_Todo } from "types";

interface Props {
  task: T_Todo;
  onStatusChange?(id: string, completed: boolean): void;
  onOpenDrawer(task: T_Todo): void;
}

export const TodoItem: React.FC<Props> = ({
  task,
  onStatusChange,
  onOpenDrawer,
}) => {
  const [checkHover, setCheckHover] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [dueDate, setDueDate] = useState(task?.dueDate);

  const { state: userState } = useContext(UserContext);

  const handleClick = () => {
    const taskUrl =
      task.url || task.sourceId
        ? `https://mail.google.com/mail/u/0/#inbox/${task.sourceId}`
        : null;
    if (taskUrl) {
      let a = document.createElement("a");
      a.target = "_blank";
      a.href = taskUrl;
      a.click();
    } else {
      showTaskDetails();
    }

    if (userState?._id) {
      recordActivity({
        data: {
          user: userState?._id,
          event: EActivityEvent.TodoClicked,
          source: "web",
          data: {
            todoId: task._id,
          },
        },
      });
    }
  };

  const changeTaskStatus = () => {
    if (task._id) {
      setCompleted(true);
    } else {
      setCompleted(false);
    }
    if (onStatusChange) {
      onStatusChange(task._id, true);
    }
    if (userState?._id) {
      recordActivity({
        data: {
          user: userState?._id,
          event: EActivityEvent.TodoClicked,
          source: "web",
          data: {
            todoId: task._id,
          },
        },
      });
    }
  };

  const handleStatusChange = (e: React.MouseEvent<HTMLDivElement, any>) => {
    e.stopPropagation();
    changeTaskStatus();
  };

  const showTaskDetails = () => {};

  const remainingDays = dueDate ? differenceInDays(dueDate, new Date()) : 0;
  const isExpiring = dueDate ? remainingDays <= 3 : false;
  const getDueDateColor = () => {
    if (isExpiring) {
      return "danger";
    }
    return "seconday";
  };

  useEffect(() => {
    if (task?.dueDate) {
      setDueDate(task.dueDate);
    }
  }, [task?.dueDate]);

  const showCompletedState = completed;

  return (
    <div
      className={css`
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        min-height: 70px;
        flex-shrink: 0;
        margin-bottom: 8px;
        border-radius: 8px;
        border: 1px solid #272539;
        background: #20202f;
        padding: 17px 23px;
        position: relative;
        border-width: ${showCompletedState ? 0 : 1};
        cursor: pointer;
        transition: border-color 0.4s ease-in-out;
        /* > *:not(div) {
          opacity: 0.3;
        } */
        &:hover {
          border: 1px solid #453e69;
        }
      `}
      onClick={handleClick}
    >
      <div
        className={css`
          display: flex;
          flex-direction: row;
          align-items: center;
          width: 100%;
          position: relative;
          max-width: 600px;
        `}
      >
        <div
          className={css`
            width: 24px;
            height: 24px;
            border-radius: 8px;
            border: 1px solid #403e56;
            background-color: ${checkHover
              ? "#2A2A3E"
              : false
                ? "#6A53FE"
                : "#181823"};
            cursor: pointer;
            margin-right: 24px;
            display: flex;
            transition: background-color 0.3s ease-in-out;
            left: -70px;
            z-index: 10;
            opacity: ${showCompletedState ? 0.35 : 1};
            @media (max-width: 768px) {
              display: none;
            }
          `}
          onClick={handleStatusChange}
          onMouseEnter={() => setCheckHover(true)}
          onMouseLeave={() => setCheckHover(false)}
        >
          {checkHover ? (
            <CheckIcon
              fill={checkHover ? "#403E56" : "#fff"}
              className={css`
                width: 16px;
                height: 16px;
              `}
            />
          ) : (
            <div />
          )}
        </div>
        <div
          className={css`
            width: 100%;

            white-space: wrap;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            opacity: ${showCompletedState ? 0.35 : 1};
          `}
        >
          {task?.title}
        </div>
      </div>

      {/* Right part */}
      <div
        className={css`
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-end;
          margin-left: 10px;
          opacity: ${showCompletedState ? 0.35 : 1};
        `}
      >
        <div
          className={css`
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            /* padding-right: 32px; */
            width: 55px;
            height: 30px;
            cursor: pointer;
          `}
        >
          <CalendarBlankIcon
            style={{ width: "10.5px", height: "10.5px" }}
            fill={getDueDateColor()}
          />
          <Text.Body3XS
            color={getDueDateColor()}
            style={{
              marginLeft: "8px",
            }}
            marginBottom={0}
            marginTop={0}
          >
            {dueDate ? format(dueDate, "MMM d") : "No due date"}
          </Text.Body3XS>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenDrawer(task);
          }}
          className={css`
            background: none;
            border: none;
            padding: 0;
            margin-left: 8px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: ${showCompletedState ? 0.35 : 1};
          `}
          aria-label="Open task details"
        >
          <CaretRightIcon style={{ width: 16, height: 16 }} fill="seconday" />
        </button>
      </div>
    </div>
  );
};
